import { signupSchema } from "@frontend-shared/schema/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useRegistrationStatus } from "@/api/auth/useRegistrationStatus";
import { useSignup } from "@/api/auth/useSignup";
import { FormSubmitButton } from "@/components/FormSubmitButton";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { StyledInput } from "@/components/StyledInput";
import { StyledPasswordInput } from "@/components/StyledPasswordInput";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PATHS } from "@/constants/paths";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useOnPremise } from "@/hooks/use-on-premise";
import { usePasswordFlowGuard } from "@/hooks/use-password-flow-guard";
import { clearLocalStorage, cn } from "@/lib/utils";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { AuthHeader } from "./AuthHeader";
import { AuthLayout } from "./AuthLayout";
import { AUTH_CONTROL_HEIGHT_CLASS } from "./constants/auth-controls";
import { SocialButtons } from "./SocialButtons";

const SignUp = () => {
  const navigate = useNavigate();
  const { isOnPremise } = useOnPremise();
  const { t } = useTranslation("auth");
  useDocumentTitle(t("page_title"));
  const signUpMutation = useSignup();
  const { data: registrationStatus, isLoading } = useRegistrationStatus();
  // NOTE: NSAI-6323 — signing up sets a password nobody can then sign in with,
  // and the server refuses the request outright, so an SSO-only deployment sends
  // the visitor back to sign-in. The guard also withholds the form until
  // /app-config has answered, rather than rendering it and navigating away a
  // frame later — which would show exactly the password field this feature
  // removes.
  const canRenderPasswordFlow = usePasswordFlowGuard();

  useEffect(() => {
    clearLocalStorage();
  }, []);

  // Redirect to sign-in if registration is disabled
  useEffect(() => {
    if (!isLoading && registrationStatus?.registrationEnabled === false) {
      navigate("/auth/sign-in", { replace: true });
    }
  }, [registrationStatus, isLoading, navigate]);

  const form = useForm({
    ...FORM_VALIDATION_OPTIONS,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
    resolver: zodResolver(signupSchema(t)),
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    signUpMutation.mutate(values, {
      onSuccess: () => {
        toast.success(t("sign_up.success.title"), {
          description: t(
            isOnPremise
              ? "sign_up.success.description-on-premise"
              : "sign_up.success.description-default",
          ),
        });
        navigate(PATHS.CHAT);
      },
    });
  });

  if (!canRenderPasswordFlow) {
    return null;
  }

  return (
    <AuthLayout>
      <div className="flex flex-col gap-4">
        <AuthHeader description={t("sign_up.description")} className="mb-2" />
        <SocialButtons registrationStatus={registrationStatus} />
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t("first_name.label")}</FormLabel>
                    <FormControl>
                      <StyledInput
                        className={AUTH_CONTROL_HEIGHT_CLASS}
                        placeholder={t("first_name.placeholder")}
                        {...field}
                        isError={!!error}
                        data-testid="sign-up_first-name-input"
                      />
                    </FormControl>
                    <FormMessage data-testid="sign-up_first-name-error" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field, fieldState: { error } }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t("last_name.label")}</FormLabel>
                    <FormControl>
                      <StyledInput
                        className={AUTH_CONTROL_HEIGHT_CLASS}
                        placeholder={t("last_name.placeholder")}
                        {...field}
                        isError={!!error}
                        data-testid="sign-up_last-name-input"
                      />
                    </FormControl>
                    <FormMessage data-testid="sign-up_last-name-error" />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>{t("email.label")}</FormLabel>
                  <FormControl>
                    <StyledInput
                      className={AUTH_CONTROL_HEIGHT_CLASS}
                      placeholder={t("email.placeholder")}
                      {...field}
                      isError={!!error}
                      data-testid="sign-up_email-input"
                    />
                  </FormControl>
                  <FormMessage data-testid="sign-up_email-error" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>{t("password.label")}</FormLabel>
                  <FormControl>
                    <StyledPasswordInput
                      className={AUTH_CONTROL_HEIGHT_CLASS}
                      placeholder={t("password.placeholder")}
                      {...field}
                      autoComplete="new-password"
                      isError={!!error}
                      data-testid="sign-up_password-input"
                    />
                  </FormControl>
                  <PasswordStrengthMeter
                    password={field.value}
                    data-testid="sign-up_password-strength"
                  />
                  <FormMessage data-testid="sign-up_password-error" />
                </FormItem>
              )}
            />
            <FormSubmitButton
              className={cn("w-full", AUTH_CONTROL_HEIGHT_CLASS)}
              isLoading={signUpMutation.isPending}
              data-testid="sign-up_submit-button"
            >
              {t("sign_up.submit")}
            </FormSubmitButton>
          </form>
        </Form>
        <div className="flex flex-col gap-3">
          <p
            className="text-sm/6 text-center text-balance text-foreground-secondary"
            data-testid="sign-up_signin-content"
          >
            <Trans
              t={t}
              i18nKey="sign_up.has_account"
              components={{
                SignInLink: (
                  <Link
                    to="/auth/sign-in"
                    className="text-link"
                    data-testid="sign-up_signin-link"
                  />
                ),
              }}
            />
          </p>
          {!isOnPremise && (
            <p className="text-sm/6 text-center text-balance text-foreground-secondary">
              <Trans
                t={t}
                i18nKey="terms"
                components={{
                  TermsLink: (
                    <Button className="p-0 m-0 text-link" variant="link" />
                  ),
                  PrivacyPolicyLink: (
                    <Button className="p-0 m-0 text-link" variant="link" />
                  ),
                }}
              />
            </p>
          )}
        </div>
      </div>
    </AuthLayout>
  );
};

export default SignUp;
